import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';

import SkeletonLoader from '../Loader/Loader';

import useDebounce from './useDebounce';

const CheckboxList = ({
  columns,
  data = [],
  selectedItems,
  onCheckboxChange,
  searchTerm: parentSearchTerm,
  sortBy: parentSortBy,
  sortOrder: parentSortOrder,
  unselectedBackground = 'white',
  loading,
  updateDefaultDashboardId,
  defaultDashboardId,
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState(parentSearchTerm);
  const [localSortBy, setLocalSortBy] = useState(parentSortBy);
  const [localSortOrder, setLocalSortOrder] = useState(parentSortOrder);
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    function filterAndSortData() {
      let filtered = filteredData.length > 0 ? filteredData : data;

      if (filteredData.length > 0) setFilteredData([]);

      // Filter by search term
      if (debouncedSearchTerm) {
        filtered = filtered.filter(item =>
          columns.some(col =>
            item[col.key]
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase())
          )
        );
      }

      // Sort by dropdown selection
      if (localSortBy) {
        filtered.sort((a, b) => {
          if (localSortOrder === 'asc') {
            return String(a[localSortBy]).toLowerCase() <
              String(b[localSortBy]).toLowerCase()
              ? 1
              : -1;
          } else {
            return String(a[localSortBy]).toLowerCase() >
              String(b[localSortBy]).toLowerCase()
              ? 1
              : -1;
          }
        });
      }

      setFilteredData(filtered);
    }
    filterAndSortData();
  }, [debouncedSearchTerm, localSortBy, localSortOrder, columns]);

  const sortOrderOptions = [
    { label: 'None', value: 'null' },
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
  ];

  const handleSortBy = e => {
    setLocalSortBy(e.value);
    setLocalSortOrder('null');
  };

  const renderCellValue = (item, col, index) => {
    // function to render the data
    const value = item[col.key];

    return (
      <>
        {value.length >= 26 && (
          <Tooltip target={`.custom-tooltip-${index}-${col.key}`}>
            {value}
          </Tooltip>
        )}
        <span
          className={`${col.bold === true ? 'font-bold' : 'font-normal'} ${
            col.italic === true ? 'font-italic' : 'font-normal'
          } 
          white-space-nowrap overflow-hidden text-overflow-ellipsis block w-16rem sm:w-10rem md:w-14rem xl:w-16rem custom-tooltip-${index}-${
            col.key
          }`}
        >
          {value}
        </span>
      </>
    );
  };

  return (
    <div className="w-full">
      <div className="grid mb-3">
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <span className="p-float-label">
            <InputText
              id="search"
              value={searchTerm}
              className="min-w-full"
              onChange={e => setSearchTerm(e.target.value)}
            />
            <label htmlFor="search">Search</label>
          </span>
        </div>
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <span className="p-float-label">
            <Dropdown
              id="sortBy"
              value={localSortBy}
              className="min-w-full"
              options={columns.map(col => ({
                label: col.label,
                value: col.key,
              }))}
              onChange={e => handleSortBy(e)}
              placeholder="Sort By"
            />
          </span>
        </div>
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <span className="p-float-label">
            <Dropdown
              id="sortOrder"
              value={localSortOrder}
              className="min-w-full"
              options={sortOrderOptions}
              onChange={e => setLocalSortOrder(e.value)}
              placeholder="Order"
            />
          </span>
        </div>
      </div>
      <div className={`grid overflow-x-hidden`}>
        {filteredData.length === 0 && loading === true && (
          <>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
            <div className="col-12 md:col-6 lg:col-4 h-20em pad-3">
              <SkeletonLoader
                columnCount={1}
                columnWidth="100%"
                columnHeight="12rem"
              />
            </div>
          </>
        )}
        {filteredData.map((item, index) => (
          <div
            className={`col-12 md:col-6 lg:col-4 xl:col-3 h-20em pad-3`}
            key={index}
          >
            <Card
              className={`flex align-items-center gap-2 surface-50 w-full pad-3 ${
                selectedItems.some(i => i.name === item.name)
                  ? 'pf-surface-light-primary-50'
                  : unselectedBackground
              } min-h-0 md:min-h-full md:max-h-0 md:max-h-full`}
              key={index}
              pt={{
                body: {
                  className: 'w-full p-3',
                },
              }}
              title={
                <div className="grid">
                  {/* <div className=""> */}
                  <div className="col-10">
                    <div className="flex align-items-center">
                      <Checkbox
                        inputId={`cb${index}`}
                        checked={selectedItems.some(i => i.name === item.name)}
                        onChange={() => onCheckboxChange(item)}
                      />
                      <label
                        htmlFor={`cb${index}`}
                        className={`ml-2 md:ml-1 w-10 xl:w-11 ${
                          selectedItems.some(i => i.name === item.name)
                            ? ''
                            : 'text-color-secondary'
                        }`}
                      >
                        <div className="pad-3 ">
                          {columns.map(
                            col =>
                              col.isTitle && (
                                <div
                                  className="w-full overflow-hidden"
                                  key={col.key}
                                >
                                  {renderCellValue(item, col, index)}
                                </div>
                              )
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="col-2 flex justify-content-end">
                    {updateDefaultDashboardId && (
                      <Checkbox
                        inputId={`cb${index}`}
                        checked={item.id == defaultDashboardId}
                        onChange={() => updateDefaultDashboardId(item.id)}
                        tooltip="Mark as a default"
                      />
                    )}
                  </div>
                  {/* </div> */}
                </div>
              }
              subTitle={
                <div
                  className={`card flex flex-wrap justify-content-start ${
                    selectedItems.some(i => i.name === item.name)
                      ? ''
                      : 'text-color-secondary'
                  }`}
                >
                  <div>
                    {columns.map(
                      col =>
                        col.isSubTitle && (
                          <div key={col.key}>
                            {renderCellValue(item, col, index)}
                          </div>
                        )
                    )}
                  </div>
                </div>
              }
            >
              <div
                className={`card flex flex-wrap justify-content-start ${
                  selectedItems.some(i => i.name === item.name)
                    ? ''
                    : 'text-color-secondary'
                }`}
              >
                <div>
                  {columns.map(
                    col =>
                      !col.isTitle &&
                      !col.isSubTitle && (
                        <div key={col.key}>
                          {renderCellValue(item, col, index)}
                        </div>
                      )
                  )}
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

CheckboxList.defaultProps = {
  data: [],
};

export default CheckboxList;
